// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
// Slimmed for dashboard integration: dropped the `getCurrentUser`
// async thunk and the `/auth/me` API call. The CRM no longer fetches
// its own session — CrmRoot bootstraps a brain crm-api token and
// dispatches `setAuthData` directly. All login/2FA/logout flows live
// in the dashboard.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  active: boolean;
  email_verified: boolean;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
  roles: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  is_super_admin?: boolean;
  two_factor_enabled?: boolean;
}

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  plan: string;
  logo_url?: string | null;
  company_name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  lastFetched: number | null;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  tenant: null,
  permissions: [],
  isAuthenticated: false,
  isLoading: false,
  lastFetched: null,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set authentication data (used after login)
    setAuthData: (state, action: PayloadAction<{ user: User; tenant: Tenant; permissions?: string[] }>) => {
      state.user = action.payload.user
      state.tenant = action.payload.tenant
      state.permissions = action.payload.permissions || []
      state.isAuthenticated = true
      state.lastFetched = Date.now()
      state.error = null
    },
    
    // Clear all auth data (logout)
    clearAuth: (state) => {
      state.user = null
      state.tenant = null
      state.permissions = []
      state.isAuthenticated = false
      state.lastFetched = null
      state.error = null
      state.isLoading = false
    },
    
    // Clear cache to force fresh data on next request
    clearCache: (state) => {
      state.lastFetched = null
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null
    },
    
    // Update user data (used after profile update)
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.lastFetched = Date.now()
    }
  },
})

export const { setAuthData, clearAuth, clearCache, clearError, updateUser } = authSlice.actions
export default authSlice.reducer

